import { useState, useEffect } from "react";

import {
  makeGetApiCall,
  makePostApiCall,
  makePostApiCallBlob,
} from "../../shared/makeApiCall";
import {
  downloadBlob,
  generateInventoryFilename,
} from "../../shared/downloadFile";
import FilterSingleSelectDropdown from "../../components/filterDropdown/FilterSingleSelectDropdown";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import DateFilter from "../../components/filterDropdown/DateFilter";

export function InventoryHistoryViewModel(logout) {
  const defaultFilters = {
    date: null,
    warehouse_location: { value: -1, label: "All locations" },
    product_data: { value: -1, label: "All PLUs" },
    class: { value: -1, label: "All Classes" },
    category: { value: -1, label: "All categories" },
  };
  const [filters, setFilters] = useState(defaultFilters);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [inventoryHistoryData, setInventoryHistoryData] = useState({
    data: [],
  });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [reportInProgress] = useState(false);
  const [exportInProgress, setExportInProgress] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [pluOptions, setpluOptions] = useState(false);
  const [classTypeOptions, setClassTypeOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [warehouseLocations, setWarehouseLocations] = useState(false);

  const getInventoryHistory = async function (search, page, filter) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
      filter: filter,
    };
    let callResult = await makePostApiCall(`getInventoryHistory`, payload);
    setLoading(false);
    if (callResult.success) {
      setInventoryHistoryData(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getAllPlus = async function () {
    let callResult = await makeGetApiCall(`productsForDropdown`);
    if (callResult.success) {
      setpluOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getWarehouseLocations = async function () {
    let callResult = await makeGetApiCall(`warehouseLocationsForDropdown/2`);
    if (callResult.success) {
      setWarehouseLocations(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getClassTypes = async function () {
    setLoading(true);
    let callResult = await makeGetApiCall("classesForDropdown");
    setLoading(false);
    if (callResult.success) {
      setClassTypeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch class types");
    }
  };

  const getCategoryTypes = async function () {
    if (filters.class.value === -1) {
      return;
    }
    let callResult = await makeGetApiCall(`categoriesForDropdownWithClass/${filters.class.value}`);
    if (callResult.success) {
      setCategories([
        { value: -1, label: "All categories" },
        ...callResult.data,
      ]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch category types");
    }
  };

  useEffect(() => {
    if (filters.class.value === -1) {
      setCategories([{ value: -1, label: "All categories" }]);
    } else {
      getCategoryTypes();
    }
  }, [filters.class.value]);

  const filterComponents = [
    <DateFilter
      date={filters.date}
      onChange={(name, value) =>
        setFilters({
          ...filters,
          [name]: value,
        })
      }
      maxDate={new Date()}
      resetFilter={() =>
        setFilters({
          ...filters,
          date: null,
        })
      }
    />,
    <FilterSingleSelectDropdown
      header="PLU"
      onChange={(value) =>
        setFilters({
          ...filters,
          product_data: value,
        })
      }
      options={pluOptions}
      resetFilter={() => setFilters(defaultFilters)}
      valueName="plu"
      value={filters.product_data}
    />,
    <FilterSingleSelectDropdown
      header="Class"
      onChange={(value) =>
        setFilters({
          ...filters,
          class: value,
        })
      }
      options={classTypeOptions}
      resetFilter={() => setFilters(defaultFilters)}
      valueName="class"
      value={filters.class}
    />,
    <FilterSingleSelectDropdown
      header="Category"
      onChange={(value) =>
        setFilters({
          ...filters,
          category: value,
        })
      }
      options={categories}
      resetFilter={() => setFilters(defaultFilters)}
      valueName="category"
      value={filters.category}
      disabled={filters.class.value === -1}
    />,
    <FilterSingleSelectDropdown
      header="Location"
      onChange={(value) =>
        setFilters({
          ...filters,
          warehouse_location: value,
        })
      }
      options={warehouseLocations}
      resetFilter={() => setFilters(defaultFilters)}
      valueName="warehouse_location"
      value={filters.warehouse_location}
    />,
  ];

  const resetFilters = () => {
    setFilters(defaultFilters);
    setShowFilters(false);
    getInventoryHistory(null, 1, defaultFilters);
  };

  const printInventorySummaryReport = async function (search, filter) {
    const payload = {
      search: search,
      filter: filter,
    };
    let callResult = await makePostApiCall(
      `printInventorySummaryReport`,
      payload
    );
    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
      showErrorModal(callResult.data.message);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const exportInventoryPageToExcel = async function (search, filter, page) {
    setExportInProgress(true);
    const payload = {
      page: page,
      search: search,
      filter: filter,
    };

    let callResult = await makePostApiCallBlob(
      `inventoryHistory/export`,
      payload
    );
    setExportInProgress(false);

    if (callResult.success) {
      // try to determine PLU for filename
      let pluOrAll = "all";
      try {
        if (filter && filter.product_data && filter.product_data.plu) {
          pluOrAll = filter.product_data.plu;
        } else if (
          filter &&
          filter.product_data &&
          filter.product_data.value &&
          filter.product_data.value !== -1
        ) {
          pluOrAll = filter.product_data.value;
        }
      } catch (e) {
        pluOrAll = "all";
      }

      const filename = generateInventoryFilename(pluOrAll);
      downloadBlob(
        callResult.data,
        filename,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
      return { success: true, filename };
    } else {
      if (callResult.errorStatus === 401) {
        logout();
        return { success: false, errorStatus: 401 };
      }
      showErrorModal(
        "Export failed",
        callResult.data && callResult.data.subtitle
          ? callResult.data.subtitle
          : null
      );
      return { success: false };
    }
  };

  const printSubItems = async function (item) {
    let callResult = await makePostApiCall(`printSubItemsForInventory`, item);
    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
      showErrorModal(callResult.data.message);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  return {
    emptyModal,
    getInventoryHistory,
    loading,
    inventoryHistoryData,
    setSlideOver,
    slideOver,
    setEmptyModelOpen,
    showReports,
    setShowReports,
    reportInProgress,
    exportInProgress,
    showFilters,
    setShowFilters,
    filters,
    setFilters,
    filterComponents,
    resetFilters,
    getAllPlus,
    printInventorySummaryReport,
    printSubItems,
    exportInventoryPageToExcel,
    pluOptions,
    classTypeOptions,
    categories,
    warehouseLocations,
    getWarehouseLocations,
    getClassTypes,
    getCategoryTypes,
  };
}
